import React from 'react';
import { Typography } from '../Typography';
import { twMerge } from 'tailwind-merge';

export interface RadioGroupProps<T = string | number> {
  value: T;
  onChange: (value: T) => void;
  options: { label: string; value: T; content?: React.ReactNode }[];
  name?: string;
  className?: string;
  optionsClassName?: string;
  direction?: 'row' | 'column';
}

function RadioGroup<T = string | number>({
  value,
  onChange,
  options,
  name,
  direction = 'row',
  className = '',
  optionsClassName = '',
}: RadioGroupProps<T>) {
  return (
    <div
      className={twMerge(
        'flex flex-wrap justify-center gap-4',
        direction === 'row' ? 'flex-row' : 'flex-col',
        className,
      )}
    >
      {options.map((option) => {
        const isChecked = value === option.value;
        return (
          <label
            key={`radio-option-${option.value}`}
            className={twMerge(
              'group/radio-group flex cursor-pointer items-center gap-3',
              optionsClassName,
            )}
            data-selected={isChecked}
            // Re-trigger the change handler when the active option is clicked again.
            onClick={() => {
              if (isChecked) onChange(option.value);
            }}
            // Re-trigger the change handler when the active option is clicked again via keyboard.
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (isChecked) onChange(option.value);
              }
            }}
          >
            <input
              type="radio"
              name={name}
              value={String(option.value)}
              checked={isChecked}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            <span
              className={`relative flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-1 transition-all duration-200 group-hover/radio-group:bg-blue-100 ${
                isChecked
                  ? 'bg-blue-25 border-blue-700 focus:shadow-[0_0_0_4px_rgba(37,99,235,0.15)]'
                  : 'border-grey-300 bg-white'
              }`}
            >
              {isChecked && (
                <span className="h-2 w-2 rounded-full bg-blue-700" />
              )}
            </span>
            <Typography className="text-gray-800">{option.label}</Typography>
            {option.content && <div className="ml-2">{option.content}</div>}
          </label>
        );
      })}
    </div>
  );
}

export default RadioGroup;
